import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { icuAPI } from '../../utils/api';

const VENT_STATUS = ['None', 'Intubated', 'Non-Invasive (BiPAP)', 'Non-Invasive (CPAP)', 'High-Flow O2', 'Weaning'];
const NOTE_TYPES = ['Progress', 'Doctor Round', 'Nurse Note', 'Procedure', 'Family Update'];
const inputStyle = { width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' };

function VitalInput({ label, field, value, onChange, unit, placeholder }) {
    return (
        <div>
            <label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 4 }}>{label}{unit ? ` (${unit})` : ''}</label>
            <input type="number" step="any" style={inputStyle} value={value} onChange={e => onChange(field, e.target.value)} placeholder={placeholder || ''} />
        </div>
    );
}

export default function ICUPatient() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [admission, setAdmission] = useState(null);
    const [vitals, setVitals] = useState([]);
    const [notes, setNotes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [tab, setTab] = useState('vitals');
    const [showVitalsForm, setShowVitalsForm] = useState(false);
    const [showNoteForm, setShowNoteForm] = useState(false);
    const [showDischarge, setShowDischarge] = useState(false);
    const [vitalsForm, setVitalsForm] = useState({ bp_systolic: '', bp_diastolic: '', heart_rate: '', spo2: '', temperature: '', rr: '', gcs: '', urine_output: '', fio2: '', notes: '' });
    const [noteForm, setNoteForm] = useState({ note_type: 'Progress', note_text: '' });
    const [dischargeNotes, setDischargeNotes] = useState('');
    const [saving, setSaving] = useState(false);

    const load = async () => {
        try {
            const res = await icuAPI.getAdmission(id);
            setAdmission(res.data.admission);
            setVitals(res.data.vitals);
            setNotes(res.data.notes);
        } catch { toast.error('Failed to load'); }
        finally { setLoading(false); }
    };

    useEffect(() => { load(); }, [id]);

    const setV = (k, v) => setVitalsForm(f => ({ ...f, [k]: v }));

    const handleVitals = async () => {
        setSaving(true);
        try {
            await icuAPI.recordVitals({ admission_id: id, patient_id: admission.patient_id, ...vitalsForm });
            toast.success('Vitals recorded');
            setShowVitalsForm(false);
            setVitalsForm({ bp_systolic: '', bp_diastolic: '', heart_rate: '', spo2: '', temperature: '', rr: '', gcs: '', urine_output: '', fio2: '', notes: '' });
            load();
        } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
        finally { setSaving(false); }
    };

    const handleNote = async () => {
        if (!noteForm.note_text.trim()) return toast.error('Enter note text');
        setSaving(true);
        try {
            await icuAPI.addNote({ admission_id: id, ...noteForm });
            toast.success('Note added');
            setShowNoteForm(false);
            setNoteForm({ note_type: 'Progress', note_text: '' });
            load();
        } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
        finally { setSaving(false); }
    };

    const handleDischarge = async () => {
        if (!dischargeNotes.trim()) return toast.error('Enter discharge notes');
        if (!window.confirm('Discharge this patient from ICU?')) return;
        setSaving(true);
        try {
            await icuAPI.discharge(id, { discharge_notes: dischargeNotes });
            toast.success('Patient discharged from ICU');
            navigate('/icu');
        } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
        finally { setSaving(false); }
    };

    const handleDeleteNote = async (noteId) => {
        if (!window.confirm('Delete this note?')) return;
        try {
            await icuAPI.deleteNote(noteId);
            toast.success('Deleted');
            load();
        } catch { toast.error('Failed'); }
    };

    const updateVent = async (v) => {
        try {
            await icuAPI.updateAdmission(id, { ventilator_status: v });
            setAdmission(a => ({ ...a, ventilator_status: v }));
            toast.success('Updated');
        } catch { toast.error('Failed'); }
    };

    const vitalColor = (val, warn, critical) => {
        const n = parseFloat(val);
        if (!val) return '#374151';
        if (critical && (n <= critical[0] || n >= critical[1])) return '#dc2626';
        if (warn && (n <= warn[0] || n >= warn[1])) return '#d97706';
        return '#16a34a';
    };

    const printICUSheet = () => {
        if (!admission) return;
        const latest = vitals[0];
        const win = window.open('', '_blank', 'width=850,height=700');
        win.document.write(`<!DOCTYPE html><html><head><title>ICU Patient Sheet</title>
        <style>
            body { font-family: Arial, sans-serif; font-size: 12px; margin: 20px; }
            h2 { color: #dc2626; border-bottom: 2px solid #dc2626; padding-bottom: 6px; }
            .grid { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; margin: 10px 0; }
            .box { background: #f8fafc; padding: 8px 12px; border-radius: 4px; border: 1px solid #e2e8f0; }
            .label { font-size: 10px; color: #64748b; font-weight: 600; text-transform: uppercase; }
            table { width: 100%; border-collapse: collapse; margin-top: 12px; font-size: 11px; }
            th, td { border: 1px solid #e2e8f0; padding: 6px 8px; text-align: left; }
            th { background: #fef2f2; font-weight: 700; }
            @media print { button { display: none; } }
        </style></head><body>
        <h2>ICU / CCU Patient Sheet</h2>
        <div class="grid">
            <div class="box"><div class="label">Patient</div><strong>${admission.full_name}</strong></div>
            <div class="box"><div class="label">MRN</div>${admission.mrn}</div>
            <div class="box"><div class="label">Bed</div><strong>${admission.bed_number}</strong></div>
            <div class="box"><div class="label">Diagnosis</div>${admission.diagnosis || '—'}</div>
            <div class="box"><div class="label">Ventilator</div>${admission.ventilator_status}</div>
            <div class="box"><div class="label">Admitted</div>${new Date(admission.admitted_at).toLocaleString()}</div>
        </div>
        ${latest ? `<h3>Latest Vitals (${new Date(latest.recorded_at).toLocaleString()})</h3>
        <div class="grid">
            <div class="box"><div class="label">BP</div>${latest.bp_systolic ?? '—'}/${latest.bp_diastolic ?? '—'} mmHg</div>
            <div class="box"><div class="label">HR</div>${latest.heart_rate ?? '—'} /min</div>
            <div class="box"><div class="label">SpO2</div>${latest.spo2 ?? '—'} %</div>
            <div class="box"><div class="label">Temp</div>${latest.temperature ?? '—'} °C</div>
            <div class="box"><div class="label">RR</div>${latest.rr ?? '—'} /min</div>
            <div class="box"><div class="label">GCS</div>${latest.gcs ?? '—'}</div>
        </div>` : ''}
        <h3>Notes</h3>
        <table><thead><tr><th>Time</th><th>Type</th><th>By</th><th>Note</th></tr></thead>
        <tbody>${notes.map(n => `<tr><td>${new Date(n.written_at).toLocaleString()}</td><td>${n.note_type}</td><td>${n.written_by}</td><td>${n.note_text}</td></tr>`).join('')}</tbody>
        </table>
        <h3>Vitals History</h3>
        <table><thead><tr><th>Time</th><th>BP</th><th>HR</th><th>SpO2</th><th>Temp</th><th>RR</th><th>GCS</th><th>UO</th></tr></thead>
        <tbody>${vitals.map(v => `<tr>
            <td>${new Date(v.recorded_at).toLocaleString()}</td>
            <td>${v.bp_systolic ?? '—'}/${v.bp_diastolic ?? '—'}</td>
            <td>${v.heart_rate ?? '—'}</td><td>${v.spo2 ?? '—'}</td>
            <td>${v.temperature ?? '—'}</td><td>${v.rr ?? '—'}</td>
            <td>${v.gcs ?? '—'}</td><td>${v.urine_output ?? '—'}</td>
        </tr>`).join('')}</tbody></table>
        <button onclick="window.print()" style="margin-top:12px;padding:8px 20px;background:#dc2626;color:white;border:none;border-radius:6px;cursor:pointer">Print</button>
        </body></html>`);
        win.document.close(); win.focus();
    };

    if (loading) return <div style={{ textAlign: 'center', padding: 60, color: '#64748b' }}>Loading...</div>;
    if (!admission) return <div style={{ textAlign: 'center', padding: 60, color: '#dc2626' }}>Patient not found</div>;

    const latest = vitals[0];

    return (
        <div>
            {/* Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <button onClick={() => navigate('/icu')} style={{ background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: 7, cursor: 'pointer', padding: '6px 12px', fontSize: 13 }}>← Back</button>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <div style={{ background: '#fef2f2', border: '2px solid #fecaca', borderRadius: 8, padding: '6px 10px', textAlign: 'center' }}>
                            <div style={{ fontSize: 9, fontWeight: 800, color: '#dc2626' }}>BED</div>
                            <div style={{ fontSize: 16, fontWeight: 800, color: '#dc2626' }}>{admission.bed_number}</div>
                        </div>
                        <div>
                            <h2 style={{ fontSize: 18, fontWeight: 700, color: '#dc2626', margin: 0 }}>{admission.full_name}</h2>
                            <p style={{ fontSize: 12, color: '#64748b', margin: 0 }}>{admission.mrn} · {admission.gender} · Age {admission.age_years}</p>
                        </div>
                    </div>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                    <button onClick={printICUSheet} style={{ padding: '8px 14px', background: '#0369a1', color: 'white', border: 'none', borderRadius: 7, cursor: 'pointer', fontSize: 12 }}>🖨️ Print Sheet</button>
                    <button onClick={() => setShowVitalsForm(!showVitalsForm)} style={{ padding: '8px 14px', background: '#059669', color: 'white', border: 'none', borderRadius: 7, cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>+ Vitals</button>
                    <button onClick={() => setShowNoteForm(!showNoteForm)} style={{ padding: '8px 14px', background: '#7c3aed', color: 'white', border: 'none', borderRadius: 7, cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>+ Note</button>
                    {admission.status === 'Active' && (
                        <button onClick={() => setShowDischarge(!showDischarge)} style={{ padding: '8px 14px', background: '#dc2626', color: 'white', border: 'none', borderRadius: 7, cursor: 'pointer', fontSize: 12 }}>Discharge</button>
                    )}
                </div>
            </div>

            {/* Info + Ventilator */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5,1fr)', gap: 10, marginBottom: 16 }}>
                {[
                    { label: 'Diagnosis', value: admission.diagnosis || '—' },
                    { label: 'Admitted From', value: admission.admission_source },
                    { label: 'Admitting Doctor', value: admission.admitting_doctor || '—' },
                    { label: 'Status', value: admission.status },
                    { label: 'Time Admitted', value: new Date(admission.admitted_at).toLocaleDateString('en-PK') },
                ].map(item => (
                    <div key={item.label} style={{ background: 'white', borderRadius: 8, padding: '10px 12px', border: '1px solid #e2e8f0' }}>
                        <div style={{ fontSize: 10, fontWeight: 600, color: '#64748b', textTransform: 'uppercase', marginBottom: 4 }}>{item.label}</div>
                        <div style={{ fontSize: 12, fontWeight: 600, color: '#1e293b' }}>{item.value}</div>
                    </div>
                ))}
            </div>

            {/* Ventilator Status */}
            <div style={{ background: 'white', borderRadius: 10, padding: '14px 18px', marginBottom: 16, border: '1px solid #e2e8f0' }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#374151', marginBottom: 10 }}>Ventilator / Respiratory Support</div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    {VENT_STATUS.map(v => (
                        <button key={v} onClick={() => updateVent(v)}
                            style={{ padding: '6px 14px', borderRadius: 20, border: `2px solid ${admission.ventilator_status === v ? '#dc2626' : '#e2e8f0'}`,
                                background: admission.ventilator_status === v ? '#fef2f2' : 'white',
                                color: admission.ventilator_status === v ? '#dc2626' : '#374151',
                                cursor: 'pointer', fontSize: 12, fontWeight: admission.ventilator_status === v ? 700 : 400 }}>
                            {v}
                        </button>
                    ))}
                </div>
            </div>

            {/* Latest Vitals Summary */}
            {latest && (
                <div style={{ background: 'white', borderRadius: 10, padding: '14px 18px', marginBottom: 16, border: '1px solid #e2e8f0' }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: '#374151', marginBottom: 12 }}>Latest Vitals — {new Date(latest.recorded_at).toLocaleString('en-PK')}</div>
                    <div style={{ display: 'flex', gap: 24 }}>
                        {[
                            { label: 'BP', value: latest.bp_systolic ? `${latest.bp_systolic}/${latest.bp_diastolic}` : null, unit: 'mmHg', warn: null },
                            { label: 'HR', value: latest.heart_rate, unit: '/min', warn: [60,100], crit: [40,140] },
                            { label: 'SpO2', value: latest.spo2, unit: '%', warn: [94,100], crit: [88,100] },
                            { label: 'Temp', value: latest.temperature, unit: '°C', warn: [36.5,37.5], crit: [35,39] },
                            { label: 'RR', value: latest.rr, unit: '/min', warn: [12,20], crit: [8,30] },
                            { label: 'GCS', value: latest.gcs, unit: '', warn: [13,15], crit: [8,15] },
                            { label: 'UO', value: latest.urine_output, unit: 'ml/hr', warn: null },
                            { label: 'FiO2', value: latest.fio2, unit: '%', warn: null },
                        ].map(v => (
                            <div key={v.label} style={{ textAlign: 'center' }}>
                                <div style={{ fontSize: 10, color: '#94a3b8', fontWeight: 600, marginBottom: 4 }}>{v.label}</div>
                                <div style={{ fontWeight: 700, fontSize: 16, color: v.value ? vitalColor(v.value, v.warn, v.crit) : '#94a3b8' }}>
                                    {v.value ?? '—'}
                                </div>
                                {v.value && v.unit && <div style={{ fontSize: 10, color: '#94a3b8' }}>{v.unit}</div>}
                            </div>
                        ))}
                    </div>
                    {latest.notes && <div style={{ fontSize: 12, color: '#64748b', marginTop: 10, borderTop: '1px solid #f1f5f9', paddingTop: 8 }}>Note: {latest.notes}</div>}
                </div>
            )}

            {/* Vitals Form */}
            {showVitalsForm && (
                <div style={{ background: 'white', borderRadius: 10, padding: 20, marginBottom: 16, border: '1px solid #bbf7d0' }}>
                    <h3 style={{ fontSize: 14, fontWeight: 700, color: '#059669', marginBottom: 16 }}>Record Vitals</h3>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 12, marginBottom: 12 }}>
                        <VitalInput label="BP Systolic" field="bp_systolic" value={vitalsForm.bp_systolic} onChange={setV} unit="mmHg" placeholder="120" />
                        <VitalInput label="BP Diastolic" field="bp_diastolic" value={vitalsForm.bp_diastolic} onChange={setV} unit="mmHg" placeholder="80" />
                        <VitalInput label="Heart Rate" field="heart_rate" value={vitalsForm.heart_rate} onChange={setV} unit="/min" placeholder="72" />
                        <VitalInput label="SpO2" field="spo2" value={vitalsForm.spo2} onChange={setV} unit="%" placeholder="98" />
                        <VitalInput label="Temperature" field="temperature" value={vitalsForm.temperature} onChange={setV} unit="°C" placeholder="37.0" />
                        <VitalInput label="Resp Rate" field="rr" value={vitalsForm.rr} onChange={setV} unit="/min" placeholder="16" />
                        <VitalInput label="GCS" field="gcs" value={vitalsForm.gcs} onChange={setV} placeholder="15" />
                        <VitalInput label="Urine Output" field="urine_output" value={vitalsForm.urine_output} onChange={setV} unit="ml/hr" placeholder="50" />
                        <VitalInput label="FiO2" field="fio2" value={vitalsForm.fio2} onChange={setV} unit="%" placeholder="21" />
                        <div style={{ gridColumn: 'span 3' }}>
                            <label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 4 }}>Notes</label>
                            <input style={inputStyle} value={vitalsForm.notes} onChange={e => setV('notes', e.target.value)} placeholder="Any remarks..." />
                        </div>
                    </div>
                    <div style={{ display: 'flex', gap: 10 }}>
                        <button onClick={handleVitals} disabled={saving}
                            style={{ padding: '8px 24px', background: saving ? '#9ca3af' : '#059669', color: 'white', border: 'none', borderRadius: 7, cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
                            {saving ? 'Saving...' : 'Save Vitals'}
                        </button>
                        <button onClick={() => setShowVitalsForm(false)} style={{ padding: '8px 16px', background: '#f1f5f9', color: '#374151', border: '1px solid #e2e8f0', borderRadius: 7, cursor: 'pointer', fontSize: 13 }}>Cancel</button>
                    </div>
                </div>
            )}

            {/* Note Form */}
            {showNoteForm && (
                <div style={{ background: 'white', borderRadius: 10, padding: 20, marginBottom: 16, border: '1px solid #ddd6fe' }}>
                    <h3 style={{ fontSize: 14, fontWeight: 700, color: '#7c3aed', marginBottom: 14 }}>Add Note</h3>
                    <div style={{ display: 'grid', gridTemplateColumns: '200px 1fr', gap: 12, marginBottom: 14 }}>
                        <div>
                            <label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 4 }}>Note Type</label>
                            <select style={{ ...inputStyle, background: 'white' }} value={noteForm.note_type} onChange={e => setNoteForm(f => ({ ...f, note_type: e.target.value }))}>
                                {NOTE_TYPES.map(t => <option key={t}>{t}</option>)}
                            </select>
                        </div>
                        <div>
                            <label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 4 }}>Note *</label>
                            <textarea style={{ ...inputStyle, height: 80, resize: 'vertical' }} value={noteForm.note_text}
                                onChange={e => setNoteForm(f => ({ ...f, note_text: e.target.value }))}
                                placeholder="Clinical note, observations, plan..." />
                        </div>
                    </div>
                    <div style={{ display: 'flex', gap: 10 }}>
                        <button onClick={handleNote} disabled={saving}
                            style={{ padding: '8px 24px', background: saving ? '#9ca3af' : '#7c3aed', color: 'white', border: 'none', borderRadius: 7, cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
                            {saving ? 'Saving...' : 'Add Note'}
                        </button>
                        <button onClick={() => setShowNoteForm(false)} style={{ padding: '8px 16px', background: '#f1f5f9', color: '#374151', border: '1px solid #e2e8f0', borderRadius: 7, cursor: 'pointer', fontSize: 13 }}>Cancel</button>
                    </div>
                </div>
            )}

            {/* Discharge Form */}
            {showDischarge && (
                <div style={{ background: '#fef2f2', borderRadius: 10, padding: 20, marginBottom: 16, border: '1px solid #fecaca' }}>
                    <h3 style={{ fontSize: 14, fontWeight: 700, color: '#dc2626', marginBottom: 12 }}>Discharge from ICU</h3>
                    <textarea style={{ ...inputStyle, height: 80, resize: 'vertical' }} value={dischargeNotes}
                        onChange={e => setDischargeNotes(e.target.value)}
                        placeholder="Discharge condition, destination (Ward/Home/Expired), handover notes..." />
                    <div style={{ display: 'flex', gap: 10, marginTop: 12 }}>
                        <button onClick={handleDischarge} disabled={saving}
                            style={{ padding: '8px 24px', background: saving ? '#9ca3af' : '#dc2626', color: 'white', border: 'none', borderRadius: 7, cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
                            {saving ? 'Processing...' : 'Confirm Discharge'}
                        </button>
                        <button onClick={() => setShowDischarge(false)} style={{ padding: '8px 16px', background: '#f1f5f9', color: '#374151', border: '1px solid #e2e8f0', borderRadius: 7, cursor: 'pointer', fontSize: 13 }}>Cancel</button>
                    </div>
                </div>
            )}

            {/* Tabs */}
            <div style={{ display: 'flex', gap: 4, marginBottom: 16 }}>
                {[['vitals', `Vitals History (${vitals.length})`], ['notes', `Notes (${notes.length})`]].map(([t, label]) => (
                    <button key={t} onClick={() => setTab(t)}
                        style={{ padding: '8px 20px', borderRadius: 8, border: `2px solid ${tab === t ? '#dc2626' : '#e2e8f0'}`,
                            background: tab === t ? '#fef2f2' : 'white', color: tab === t ? '#dc2626' : '#374151',
                            cursor: 'pointer', fontSize: 13, fontWeight: tab === t ? 700 : 400 }}>
                        {label}
                    </button>
                ))}
            </div>

            {/* Vitals History */}
            {tab === 'vitals' && (
                <div style={{ background: 'white', borderRadius: 10, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
                    {vitals.length === 0 ? (
                        <div style={{ padding: 30, textAlign: 'center', color: '#94a3b8' }}>No vitals recorded yet</div>
                    ) : (
                        <div style={{ overflowX: 'auto' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 800 }}>
                                <thead>
                                    <tr style={{ background: '#f8fafc', fontSize: 11, color: '#64748b', fontWeight: 600 }}>
                                        {['Time', 'BP', 'HR', 'SpO2', 'Temp', 'RR', 'GCS', 'UO', 'FiO2', 'By', 'Notes'].map(h => (
                                            <th key={h} style={{ padding: '10px 12px', textAlign: 'left' }}>{h}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {vitals.map(v => (
                                        <tr key={v.id} style={{ borderTop: '1px solid #f1f5f9', fontSize: 12 }}>
                                            <td style={{ padding: '8px 12px', color: '#64748b' }}>{new Date(v.recorded_at).toLocaleString('en-PK')}</td>
                                            <td style={{ padding: '8px 12px', fontWeight: 600 }}>{v.bp_systolic ?? '—'}/{v.bp_diastolic ?? '—'}</td>
                                            <td style={{ padding: '8px 12px', fontWeight: 600, color: vitalColor(v.heart_rate, [60,100], [40,140]) }}>{v.heart_rate ?? '—'}</td>
                                            <td style={{ padding: '8px 12px', fontWeight: 600, color: vitalColor(v.spo2, [94,100], [88,100]) }}>{v.spo2 ?? '—'}</td>
                                            <td style={{ padding: '8px 12px', fontWeight: 600, color: vitalColor(v.temperature, [36.5,37.5], [35,39]) }}>{v.temperature ?? '—'}</td>
                                            <td style={{ padding: '8px 12px' }}>{v.rr ?? '—'}</td>
                                            <td style={{ padding: '8px 12px', fontWeight: 600, color: vitalColor(v.gcs, [13,15], [8,15]) }}>{v.gcs ?? '—'}</td>
                                            <td style={{ padding: '8px 12px' }}>{v.urine_output ?? '—'}</td>
                                            <td style={{ padding: '8px 12px' }}>{v.fio2 ?? '—'}</td>
                                            <td style={{ padding: '8px 12px', color: '#64748b' }}>{v.recorded_by}</td>
                                            <td style={{ padding: '8px 12px', color: '#64748b' }}>{v.notes || '—'}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            )}

            {/* Notes */}
            {tab === 'notes' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {notes.length === 0 ? (
                        <div style={{ background: 'white', borderRadius: 10, padding: 30, textAlign: 'center', color: '#94a3b8', border: '1px solid #e2e8f0' }}>No notes yet</div>
                    ) : notes.map(n => (
                        <div key={n.id} style={{ background: 'white', borderRadius: 10, padding: '14px 18px', border: '1px solid #e2e8f0' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                                <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                                    <span style={{ background: '#eff6ff', color: '#1d4ed8', borderRadius: 20, padding: '2px 10px', fontSize: 11, fontWeight: 700 }}>{n.note_type}</span>
                                    <span style={{ fontSize: 12, color: '#64748b' }}>{n.written_by} — {new Date(n.written_at).toLocaleString('en-PK')}</span>
                                </div>
                                <button onClick={() => handleDeleteNote(n.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#dc2626', fontSize: 16 }}>🗑</button>
                            </div>
                            <div style={{ fontSize: 13, color: '#1e293b', whiteSpace: 'pre-wrap' }}>{n.note_text}</div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

